<template>
  <div id="brilliant-enroll-form" class="form-contain">
    <p v-if="submitError" class="submit-error">{{ submitError }}</p>

    <form class="form-container" novalidate @submit.prevent="submit">
      <p class="section-title">Join now for free to enjoy Premium Wifi, Dining Discounts, Exclusive Member Room Rates and more!</p>

      <div class="two-wrapper">
        <div class="half">
          <div class="field">
            <input
              type="text"
              id="first_name"
              v-model="form.first_name"
              class="input-style"
              :class="{ 'has-content': form.first_name }"
            />
            <label for="first_name" class="label-style">First Name/Given Name*</label>
          </div>
          <span class="error-text" :class="{ visible: errors.first_name }">Please remember to fill in your first name</span>
        </div>
        <div class="half">
          <div class="field">
            <input
              type="text"
              id="last_name"
              v-model="form.last_name"
              class="input-style"
              :class="{ 'has-content': form.last_name }"
            />
            <label for="last_name" class="label-style">Last Name/Family Name*</label>
          </div>
          <span class="error-text" :class="{ visible: errors.last_name }">It looks like you've forgotten to fill in your surname</span>
        </div>
      </div>

      <div class="two-wrapper">
        <div class="half">
          <div class="field">
            <select v-model="form.country_prefix" class="input-style has-content" aria-label="Country Region Code">
              <option v-for="country in countries" :key="'prefix-' + country.code" :value="country.code">
                {{ country.name }}  {{ country.ISD_code }}
              </option>
            </select>
            <label class="label-style">Country/Region Code*</label>
          </div>
        </div>
        <div class="half">
          <div class="field">
            <input
              type="tel"
              id="mobile_number"
              v-model="form.mobile"
              class="input-style"
              :class="{ 'has-content': form.mobile }"
            />
            <label for="mobile_number" class="label-style">Mobile Number*</label>
          </div>
          <span class="error-text" :class="{ visible: errors.mobile }">Don't forget to fill in your mobile number, so we can get in touch</span>
        </div>
      </div>

      <div class="two-wrapper">
        <div class="half">
          <div class="field">
            <input
              type="email"
              id="join_email_address"
              v-model="form.email"
              class="input-style"
              :class="{ 'has-content': form.email }"
            />
            <label for="join_email_address" class="label-style">Email Address*</label>
          </div>
          <span class="error-text" :class="{ visible: errors.email_required }">Don't forget to fill in your email address so we know where to contact you</span>
          <span class="error-text" :class="{ visible: errors.email_invalid }">Please provide a valid email address</span>
        </div>
        <div class="half">
          <div class="field">
            <select v-model="form.country" class="input-style has-content" aria-label="Location">
              <option v-for="country in countries" :key="'loc-' + country.code" :value="country.code">{{ country.name }}</option>
            </select>
            <label class="label-style">Location*</label>
          </div>
        </div>
      </div>

      <div class="two-wrapper">
        <div class="half">
          <div class="field">
            <input
              type="password"
              id="password-join"
              v-model="form.password"
              class="input-style"
              :class="{ 'has-content': form.password }"
            />
            <label for="password-join" class="label-style">Password*</label>
          </div>
          <span class="error-text password-rules" :class="{ visible: errors.password }">
            Password requirements:
            <ul>
              <li>At least 8 valid characters</li>
              <li>A combination of letters (a-z,A-Z) and numbers (0-9)</li>
              <li>At least one upper case and one lower case</li>
              <li>At least one special character: @$!%*#?&amp;</li>
            </ul>
          </span>
        </div>
        <div class="half">
          <div class="field">
            <input
              type="password"
              id="confirm_password"
              v-model="form.confirm_password"
              class="input-style"
              :class="{ 'has-content': form.confirm_password }"
            />
            <label for="confirm_password" class="label-style">Confirm Password*</label>
          </div>
          <span class="error-text" :class="{ visible: errors.confirm_password }">Sorry, the password you entered is incorrect</span>
        </div>
      </div>

      <fieldset class="checkbox">
        <input type="checkbox" id="accept-form-brilliant" v-model="form.tc_internet" />
        <label for="accept-form-brilliant">I acknowledge and agree to the Brilliant by Langham Terms and Conditions*.</label>
      </fieldset>
      <span class="error-text block-error" :class="{ visible: errors.tc_internet }">Please read and agree to the Brilliant by Langham Terms and Conditions</span>

      <fieldset class="checkbox">
        <input type="checkbox" id="accept-form-marketing" v-model="form.tc" />
        <label for="accept-form-marketing">I would like to sign up for news and marketing updates and consent to the use of my personal data for direct marketing purposes in accordance with the Privacy Policy.</label>
      </fieldset>

      <fieldset class="checkbox">
        <input type="checkbox" id="accept-form-data" v-model="form.privacy" />
        <label for="accept-form-data">I understand and consent to the processing of my personal data (including my sensitive personal data and that of minors under my guardianship); and provision of such personal data to local and overseas recipients, in accordance with Privacy Policy*.</label>
      </fieldset>
      <span class="error-text block-error" :class="{ visible: errors.privacy }">Please read and agree to the Brilliant by Langham Privacy Policy</span>

      <fieldset class="checkbox">
        <input type="checkbox" id="accept-form-info" v-model="form.info" />
        <label for="accept-form-info">I confirm the information provided is correct*.</label>
      </fieldset>
      <span class="error-text block-error" :class="{ visible: errors.info }">Please read and agree with the above statement.</span>

      <button type="submit" class="solid-button center top-bottom-margin" :disabled="submitting">
        {{ submitting ? 'SUBMITTING...' : 'JOIN NOW' }}
      </button>
    </form>
  </div>
</template>

<script>
import { HTTP } from '@/utilities/http';
import { LANGHAM_COUNTRIES } from '@/components/custom/_langham2026/langhamCountries';

export default {
  name: 'langham-brilliant-form',
  props: {
    nseid: { type: String, required: true },
    mac_address: { type: String, required: true },
  },
  data() {
    return {
      countries: LANGHAM_COUNTRIES,
      submitting: false,
      submitError: null,
      form: {
        first_name: '',
        last_name: '',
        email: '',
        country_prefix: 'US',
        country: 'US',
        mobile: '',
        password: '',
        confirm_password: '',
        tc_internet: false,
        tc: false,
        privacy: false,
        info: false,
      },
      errors: {},
    };
  },
  methods: {
    validEmail(email) {
      const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      return re.test(email);
    },
    validatePassword(pw) {
      return /[A-Z]/.test(pw)
        && /[a-z]/.test(pw)
        && /[0-9]/.test(pw)
        && /[^A-Za-z0-9]/.test(pw)
        && pw.length >= 8;
    },
    findCountry(code) {
      return this.countries.find(c => c.code === code);
    },
    validate() {
      this.errors = {};
      let valid = true;

      if (!this.form.first_name) {
        this.errors.first_name = true;
        valid = false;
      }
      if (!this.form.last_name) {
        this.errors.last_name = true;
        valid = false;
      }
      if (!this.form.email) {
        this.errors.email_required = true;
        valid = false;
      } else if (!this.validEmail(this.form.email)) {
        this.errors.email_invalid = true;
        valid = false;
      }
      if (!this.form.mobile) {
        this.errors.mobile = true;
        valid = false;
      }
      if (!this.validatePassword(this.form.password)) {
        this.errors.password = true;
        valid = false;
      }
      if (this.form.password !== this.form.confirm_password) {
        this.errors.confirm_password = true;
        valid = false;
      }
      if (!this.form.tc_internet) {
        this.errors.tc_internet = true;
        valid = false;
      }
      if (!this.form.privacy) {
        this.errors.privacy = true;
        valid = false;
      }
      if (!this.form.info) {
        this.errors.info = true;
        valid = false;
      }

      return valid;
    },
    scrollToFirstError() {
      this.$nextTick(function () {
        const root = this.$el;
        const firstError = root && root.querySelector('.error-text.visible');
        if (firstError) {
          firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else if (root) {
          root.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }.bind(this));
    },
    submit() {
      if (this.submitting) return;

      if (!this.validate()) {
        this.submitError = 'Please complete all required fields.';
        this.scrollToFirstError();
        return;
      }

      const prefixCountry = this.findCountry(this.form.country_prefix);
      if (!prefixCountry) {
        this.submitError = 'Invalid country selection.';
        return;
      }

      this.submitError = null;
      this.submitting = true;

      const payload = {
        nseid: this.nseid,
        mac_address: this.mac_address,
        user: {
          email: this.form.email,
          first_name: this.form.first_name,
          last_name: this.form.last_name,
        },
        country: this.form.country,
        country_code: String(prefixCountry.ISD_code),
        mobile: this.form.mobile,
        password: this.form.password,
        tc_internet: this.form.tc_internet,
        tc: this.form.tc,
        privacy: this.form.privacy,
        info: this.form.info,
      };

      HTTP.post('langham-enroll', payload).then(function () {
        this.submitting = false;
        this.$emit('enrolled');
      }.bind(this)).catch(function (error) {
        this.submitting = false;
        if (error.response && error.response.data) {
          if (error.response.data.message) {
            this.submitError = error.response.data.message;
          } else if (error.response.data.invalid_fields) {
            this.submitError = 'Please check the form and try again.';
          } else {
            this.submitError = 'Enrollment failed. Please try again.';
          }
        } else {
          this.submitError = 'Enrollment failed. Please try again.';
        }
      }.bind(this));
    },
  },
};
</script>

<style scoped>
.section-title {
  display: block;
  text-align: left;
  font-size: 1rem;
  line-height: 1.25rem;
  margin-bottom: 20px;
  color: #151515;
}

.block-error {
  margin-left: 30px;
  margin-top: -6px;
}

.password-rules ul {
  color: red;
  margin: 4px 0 0;
  padding-left: 1.2rem;
}

.submit-error {
  color: red;
  text-align: center;
  margin-bottom: 16px;
  font-size: 0.9rem;
}

button.solid-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
